import { useEffect, useState } from 'react';
import { useEntitlements } from '@/hooks/useEntitlements';
import { usePreference } from '@/hooks/useUserPreferences';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { deskVoice, DESK_WELCOME } from '@/lib/deskSteps';
import DeskMark from '@/components/desk/DeskMark';

export const FEATURE_META: FeatureMeta = {
  id: 'desk-welcome',
  label: 'Desk Welcome',
  description:
    'A one-time, warm first-login greeting on Your Desk that matches your plan and points you at Desk Setup — no forced tour, and it never shows twice.',
  triggerType: 'dialog',
  personas: ['seller', 'buyer_seller', 'buyer'],
};

/**
 * First-login welcome (Phase 1 of Your Desk). Shown once per user, persisted
 * cross-device via `desk-welcome-seen`. Copy comes from DESK_WELCOME so the
 * greeting follows the same persona voice as Desk Setup, and a plan change
 * never leaves stale wording behind.
 */
export default function DeskWelcome() {
  useFeatureRegistration(FEATURE_META);
  const ent = useEntitlements();
  const [seen, setSeen] = usePreference<boolean>('desk-welcome-seen', false);
  const [open, setOpen] = useState(false);

  // Wait for entitlements so the greeting matches the plan, then open once.
  useEffect(() => {
    if (!ent.isUnresolved && !seen) setOpen(true);
  }, [ent.isUnresolved, seen]);

  if (seen || ent.isUnresolved) return null;

  const welcome = DESK_WELCOME[deskVoice(ent.can)];

  const close = () => {
    setOpen(false);
    setSeen(true);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent className="sm:max-w-md desk-paper rounded-2xl border-0 p-0 overflow-hidden [&>button]:top-5 [&>button]:right-5">
        <div className="px-8 pt-10 pb-8 text-center">
          <div className="flex justify-center">
            <DeskMark className="h-10 w-14" />
          </div>
          <DialogHeader className="mt-5 space-y-3 text-center sm:text-center">
            <DialogTitle className="font-display text-3xl font-normal tracking-tight text-balance">
              {welcome.lead}
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed text-balance">
              {welcome.body}
            </DialogDescription>
          </DialogHeader>
          <Button
            onClick={close}
            variant="guide"
            className="mt-7 px-8"
          >
            Step inside
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
